import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { 
    getChessArticles, 
    createChessArticle, 
    updateChessArticle, 
    deleteChessArticle,
    deleteAllArticles,
    incrementArticleViews,
    ChessArticle 
} from '../../../../services/chesspediaService';
import { seedChesspediaToDatabase } from '../../../../utils/seedChesspedia';
import { trackChequeredArticlesViewed, trackChequeredArticleOpened, trackChequeredArticlesSeeded } from '../../../../utils/analytics';

interface ChessBasicsViewProps {
    serviceProviderId: string;
    loggedInUserId: string;
    userName: string;
    activeRole?: { role: string | null } | null;
}

// Define categories outside component as it's a constant
const categories: Array<{ id: ChessArticle['category'] | 'all'; label: string; icon: string }> = [
    { id: 'all', label: 'All Articles', icon: '📚' },
    { id: 'basics', label: 'Chess Basics', icon: '♟️' },
    { id: 'openings', label: 'Opening Strategies', icon: '🚀' },
    { id: 'castling', label: 'Castle Strategies', icon: '🏰' },
    { id: 'defense', label: 'Basic Defence', icon: '🛡️' },
    { id: 'tactics', label: 'Tactics', icon: '⚔️' },
    { id: 'endgame', label: 'Endgame', icon: '🏁' },
    { id: 'strategy', label: 'Strategy', icon: '🧠' },
    { id: 'other', label: 'Other', icon: '📝' }
];

const ChessBasicsView: React.FC<ChessBasicsViewProps> = ({
    serviceProviderId,
    loggedInUserId,
    userName,
    activeRole
}) => {
    const [articles, setArticles] = useState<ChessArticle[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedArticle, setSelectedArticle] = useState<ChessArticle | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<ChessArticle['category'] | 'all'>('all');
    const [selectedDifficulty, setSelectedDifficulty] = useState<'beginner' | 'intermediate' | 'advanced' | 'all'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [seedingArticles, setSeedingArticles] = useState(false);
    const [seedingProgress, setSeedingProgress] = useState({ current: 0, total: 0 });
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingArticle, setEditingArticle] = useState<ChessArticle | null>(null);
    const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
    const [articleCountsByCategory, setArticleCountsByCategory] = useState<Record<string, number>>({});
    const [hasCheckedForAutoSeed, setHasCheckedForAutoSeed] = useState(false);
    
    // Check if user is admin
    const isAdmin = !activeRole || activeRole?.role === 'Administrator';

    const loadArticles = useCallback(async () => {
        if (!serviceProviderId) return;
        
        setLoading(true);
        try {
            const loadedArticles = await getChessArticles(serviceProviderId, {
                category: selectedCategory,
                difficulty: selectedDifficulty,
                searchQuery: searchQuery || undefined
            });
            setArticles(loadedArticles);
            
            // Calculate category counts
            const counts: Record<string, number> = {};
            categories.forEach(cat => {
                if (cat.id !== 'all') {
                    counts[cat.id] = loadedArticles.filter(a => a.category === cat.id).length;
                }
            });
            setArticleCountsByCategory(counts);
            
            return loadedArticles;
        } catch (error) {
            console.error('Error loading articles:', error);
            toast.error('Failed to load articles');
            return [];
        } finally {
            setLoading(false);
        }
    }, [serviceProviderId, selectedCategory, selectedDifficulty, searchQuery]);

    // Auto-seed articles on first load if empty
    useEffect(() => {
        const autoSeedIfEmpty = async () => {
            if (!serviceProviderId || hasCheckedForAutoSeed) return;
            
            setHasCheckedForAutoSeed(true);
            
            // Load articles with no filters to check if database is truly empty
            try {
                const allArticles = await getChessArticles(serviceProviderId);
                
                if (allArticles.length === 0) {
                    console.log('Chessopedia is empty, auto-seeding articles...');
                    setSeedingArticles(true);
                    
                    const result = await seedChesspediaToDatabase(
                        serviceProviderId,
                        loggedInUserId,
                        userName,
                        (current, total) => {
                            setSeedingProgress({ current, total });
                        }
                    );
                    
                    if (result.success) {
                        trackChequeredArticlesSeeded({
                            service_provider_id: serviceProviderId,
                            user_id: loggedInUserId,
                            user_name: userName,
                            user_role: activeRole?.role || undefined,
                            count: result.count
                        });
                        console.log(`Auto-seeded ${result.count} articles`);
                        toast.success(`Welcome to Chessopedia! ${result.count} articles loaded.`);
                    }
                    
                    setSeedingArticles(false);
                    setSeedingProgress({ current: 0, total: 0 });
                }
            } catch (error) {
                console.error('Error during auto-seed check:', error);
            }
            
            // Load articles with current filters
            await loadArticles();
        };
        
        autoSeedIfEmpty();
    }, [serviceProviderId, hasCheckedForAutoSeed, loggedInUserId, userName, loadArticles]);

    useEffect(() => {
        if (hasCheckedForAutoSeed) {
            loadArticles();
        }
    }, [loadArticles, hasCheckedForAutoSeed]);

    // Analytics: articles page viewed (once on mount)
    useEffect(() => {
        if (!serviceProviderId || !loggedInUserId) return;
        trackChequeredArticlesViewed({
            service_provider_id: serviceProviderId,
            user_id: loggedInUserId,
            user_name: userName,
            user_role: activeRole?.role ?? undefined
        });
    }, [serviceProviderId, loggedInUserId, userName, activeRole?.role]);

    // Global escape-to-close for modals
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setSelectedArticle(null);
                setShowCreateForm(false);
                setEditingArticle(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleSeedArticles = async () => {
        if (!serviceProviderId) return;
        
        setSeedingArticles(true);
        try {
            const result = await seedChesspediaToDatabase(
                serviceProviderId,
                loggedInUserId,
                userName,
                (current, total) => {
                    setSeedingProgress({ current, total });
                }
            );
            
            if (result.success) {
                trackChequeredArticlesSeeded({
                    service_provider_id: serviceProviderId,
                    user_id: loggedInUserId,
                    user_name: userName,
                    user_role: activeRole?.role || undefined,
                    count: result.count
                });
                toast.success(`Seeded ${result.count} articles!`);
                await loadArticles();
            } else {
                toast.error('Failed to seed articles');
            }
        } catch (error) {
            console.error('Error seeding articles:', error);
            toast.error('Failed to seed articles');
        } finally {
            setSeedingArticles(false);
            setSeedingProgress({ current: 0, total: 0 });
        }
    };

    const handleArticleClick = async (article: ChessArticle) => {
        if (article.id) {
            await incrementArticleViews(serviceProviderId, article.id);
        }
        if (serviceProviderId && loggedInUserId) {
            trackChequeredArticleOpened({
                service_provider_id: serviceProviderId,
                user_id: loggedInUserId,
                user_name: userName,
                user_role: activeRole?.role || undefined,
                article_id: article.id,
                article_title: article.title
            });
        }
        setSelectedArticle(article);
    };

    const handleDeleteArticle = async (articleId: string) => {
        if (!window.confirm('Are you sure you want to delete this article?')) return;
        
        const deleted = await deleteChessArticle(serviceProviderId, articleId);
        if (deleted) {
            await loadArticles();
            if (selectedArticle?.id === articleId) {
                setSelectedArticle(null);
            }
        }
    };

    const handleDeleteAllArticles = async () => {
        if (!serviceProviderId) return;

        if (!isAdmin) {
            toast.error('Only administrators can delete all articles');
            return;
        }

        setShowDeleteAllConfirm(true);
    };

    const confirmDeleteAll = async () => {
        setShowDeleteAllConfirm(false);
        try {
            const count = await deleteAllArticles(serviceProviderId);
            if (count > 0) {
                setArticles([]);
                setSelectedArticle(null);
                setArticleCountsByCategory({});
                await loadArticles(); // Reload to ensure UI is in sync
            }
        } catch (error) {
            console.error('Error deleting all articles:', error);
            toast.error('Failed to delete all articles');
        }
    };

    const filteredArticles = articles.filter(article => {
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                article.title.toLowerCase().includes(query) ||
                article.content.toLowerCase().includes(query) ||
                article.tags?.some(tag => tag.toLowerCase().includes(query))
            );
        }
        return true;
    }).sort((a, b) => {
        // Define category order: intro to royal game, basics, strategy, defense, tactics, endgame
        const categoryOrder: Record<string, number> = {
            'basics': 1, // Will check for "intro to royal game" in title
            'strategy': 2,
            'defense': 3,
            'tactics': 4,
            'endgame': 5,
            'openings': 6,
            'castling': 7,
            'other': 8
        };

        // Check if article is "intro to royal game" - prioritize it
        const aIsIntro = a.title.toLowerCase().includes('intro to royal game') || 
                        a.title.toLowerCase().includes('introduction to royal game');
        const bIsIntro = b.title.toLowerCase().includes('intro to royal game') || 
                        b.title.toLowerCase().includes('introduction to royal game');

        // "Intro to royal game" comes first (priority 0)
        if (aIsIntro && !bIsIntro) return -1;
        if (!aIsIntro && bIsIntro) return 1;
        if (aIsIntro && bIsIntro) {
            // Both are intro, sort by title
            return a.title.localeCompare(b.title);
        }

        // Get category order
        const aOrder = categoryOrder[a.category] || 99;
        const bOrder = categoryOrder[b.category] || 99;

        // Sort by category order first
        if (aOrder !== bOrder) {
            return aOrder - bOrder;
        }

        // Within same category, sort alphabetically by title
        return a.title.localeCompare(b.title);
    });

    return (
        <div style={{ maxWidth: '1920px', margin: '0 auto', width: '100%', display: 'flex', gap: '2rem', height: '100%' }}>
            {/* Left Sidebar - Categories and Filters */}
            <div style={{ width: '350px', display: 'flex', flexDirection: 'column', gap: '1rem', flexShrink: 0 }}>
                {/* Seed Articles Button */}
                <div style={{ 
                    padding: '1rem', 
                    background: '#fef3c7', 
                    borderRadius: '0.5rem',
                    border: '1px solid #fde047'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', margin: 0 }}>
                            🌱 Seed Sample Articles
                        </p>
                        {articles.length > 0 && (
                            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#78350f', margin: 0 }}>
                                Total: {articles.length}
                            </p>
                        )}
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#78350f', marginBottom: '0.75rem' }}>
                        Generate 10 sample educational articles
                    </p>
                    
                    {seedingArticles && seedingProgress.total > 0 && (
                        <div style={{ marginBottom: '0.75rem' }}>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '0.25rem'
                            }}>
                                <span style={{ fontSize: '0.7rem', color: '#78350f', fontWeight: 500 }}>
                                    Seeding articles...
                                </span>
                                <span style={{ fontSize: '0.7rem', color: '#78350f', fontWeight: 600 }}>
                                    {seedingProgress.current} / {seedingProgress.total}
                                </span>
                            </div>
                            <div style={{
                                width: '100%',
                                height: '8px',
                                background: '#fde047',
                                borderRadius: '4px',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    width: `${(seedingProgress.current / seedingProgress.total) * 100}%`,
                                    height: '100%',
                                    background: 'linear-gradient(to right, #f59e0b, #d97706)',
                                    transition: 'width 0.3s ease',
                                    borderRadius: '4px'
                                }} />
                            </div>
                        </div>
                    )}
                    
                    <button
                        onClick={handleSeedArticles}
                        disabled={seedingArticles}
                        style={{
                            width: '100%',
                            padding: '0.5rem',
                            background: seedingArticles ? '#9ca3af' : '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            cursor: seedingArticles ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {seedingArticles ? `🌱 Seeding... ${seedingProgress.current}/${seedingProgress.total}` : '🌱 Seed Articles (10 total)'}
                    </button>
                </div>

                {/* Promotion Message for Academies */}
                <div style={{ 
                    padding: '1rem', 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                    borderRadius: '0.5rem',
                    border: '1px solid #9333ea',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '1.25rem' }}>🎥</span>
                        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'white', margin: 0 }}>
                            Create Custom Videos
                        </p>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.95)', marginBottom: '0.75rem', lineHeight: '1.5' }}>
                        Want to create educational chess videos for your academy? Get professional video production services tailored to your needs.
                    </p>
                    <div style={{
                        padding: '0.75rem',
                        background: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '0.375rem',
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                    }}>
                        <p style={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.9)', margin: '0 0 0.5rem 0', fontWeight: 500 }}>
                            Contact for Pricing:
                        </p>
                        <p style={{ 
                            fontSize: '0.875rem', 
                            color: 'white', 
                            margin: 0, 
                            fontWeight: 600,
                            wordBreak: 'break-word',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            <span>📧</span>
                            <span>support@hosaksham.com</span>
                        </p>
                    </div>
                </div>

                {/* Create Article Button */}
                <button
                    onClick={() => {
                        setEditingArticle(null);
                        setShowCreateForm(true);
                    }}
                    style={{
                        width: '100%',
                        padding: '0.75rem',
                        background: '#9333ea',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                    }}
                >
                    <span>➕</span>
                    <span>Create Article</span>
                </button>

                {/* Delete All Articles Button (Admin Only) */}
                {isAdmin && articles.length > 0 && (
                    <button
                        onClick={handleDeleteAllArticles}
                        style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        <span>🗑️</span>
                        <span>Delete All Articles</span>
                    </button>
                )}

                {/* Category Filter */}
                <div style={{ 
                    padding: '1rem', 
                    background: 'white', 
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb'
                }}>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>
                        Categories
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {categories.map((category) => {
                            const count = category.id === 'all' 
                                ? articles.length 
                                : articleCountsByCategory[category.id] || 0;
                            return (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    style={{
                                        padding: '0.5rem',
                                        background: selectedCategory === category.id ? '#f3e8ff' : 'transparent',
                                        border: `1px solid ${selectedCategory === category.id ? '#9333ea' : '#e5e7eb'}`,
                                        borderRadius: '0.375rem',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: selectedCategory === category.id ? '#7c3aed' : '#374151',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span>{category.icon}</span>
                                        <span>{category.label}</span>
                                    </div>
                                    {count > 0 && (
                                        <span style={{
                                            padding: '0.125rem 0.375rem',
                                            borderRadius: '0.25rem',
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            background: selectedCategory === category.id ? '#9333ea' : '#e5e7eb',
                                            color: selectedCategory === category.id ? 'white' : '#374151'
                                        }}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Difficulty Filter */}
                <div style={{ 
                    padding: '1rem', 
                    background: 'white', 
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb'
                }}>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827', marginBottom: '0.75rem' }}>
                        Difficulty
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {(['all', 'beginner', 'intermediate', 'advanced'] as const).map((level) => (
                            <button
                                key={level}
                                onClick={() => setSelectedDifficulty(level)}
                                style={{
                                    padding: '0.5rem',
                                    background: selectedDifficulty === level ? '#f3e8ff' : 'transparent',
                                    border: `1px solid ${selectedDifficulty === level ? '#9333ea' : '#e5e7eb'}`,
                                    borderRadius: '0.375rem',
                                    fontSize: '0.75rem',
                                    fontWeight: 500,
                                    color: selectedDifficulty === level ? '#7c3aed' : '#374151',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    textTransform: 'capitalize'
                                }}
                            >
                                {level === 'all' ? 'All Levels' : level}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Search */}
                <div style={{ 
                    padding: '1rem', 
                    background: 'white', 
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb'
                }}>
                    <input
                        type="text"
                        placeholder="Search articles..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem'
                        }}
                    />
                </div>

            </div>

            {/* Right Side - Articles List */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', overflow: 'hidden' }}>
                <div style={{ 
                    flex: 1,
                    overflowY: 'auto', 
                    padding: '1rem', 
                    background: 'white', 
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e7eb',
                    minHeight: '400px'
                }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                            <p style={{ fontSize: '0.75rem' }}>Loading articles...</p>
                        </div>
                    ) : filteredArticles.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📖</div>
                            <p style={{ fontSize: '0.875rem' }}>No articles found</p>
                            <p style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>Click "Seed Articles" to get started</p>
                        </div>
                    ) : (
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
                            gap: '1.25rem' 
                        }}>
                            {filteredArticles.map((article) => {
                                // Extract first video thumbnail if available
                                const getYouTubeId = (url: string): string | null => {
                                    if (!url || !url.trim()) return null;
                                    const trimmed = url.trim();
                                    
                                    // More comprehensive patterns to match various YouTube URL formats
                                    const patterns = [
                                        // Standard watch URLs: youtube.com/watch?v=VIDEO_ID
                                        /(?:youtube\.com\/watch\?v=|youtube\.com\/watch\?.*&v=)([a-zA-Z0-9_-]{11})/,
                                        // Short URLs: youtu.be/VIDEO_ID
                                        /youtu\.be\/([a-zA-Z0-9_-]{11})/,
                                        // Embed URLs: youtube.com/embed/VIDEO_ID
                                        /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
                                        // Shorts URLs: youtube.com/shorts/VIDEO_ID
                                        /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
                                        // Direct video ID (11 characters)
                                        /^([a-zA-Z0-9_-]{11})$/,
                                        // URL with extra parameters: extract first 11-char alphanumeric sequence
                                        /[?&\/]([a-zA-Z0-9_-]{11})/
                                    ];
                                    
                                    for (const pattern of patterns) {
                                        const match = trimmed.match(pattern);
                                        if (match && match[1] && match[1].length === 11) {
                                            return match[1];
                                        }
                                    }
                                    
                                    // Fallback: try to find any 11-character alphanumeric sequence
                                    const fallbackMatch = trimmed.match(/([a-zA-Z0-9_-]{11})/);
                                    if (fallbackMatch && fallbackMatch[1]) {
                                        return fallbackMatch[1];
                                    }
                                    
                                    return null;
                                };

                                // Validate video ID format (YouTube IDs are always 11 characters)
                                const isValidVideoId = (id: string | null): boolean => {
                                    return id !== null && id.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(id);
                                };

                                // Get first valid video ID
                                let firstVideoId: string | null = null;
                                if (article.youtubeLinks && article.youtubeLinks.length > 0) {
                                    for (const url of article.youtubeLinks) {
                                        if (url && url.trim()) {
                                            const videoId = getYouTubeId(url);
                                            if (isValidVideoId(videoId)) {
                                                firstVideoId = videoId;
                                                break;
                                            }
                                        }
                                    }
                                }
                                
                                const videoCount = article.youtubeLinks?.filter(url => {
                                    if (!url || !url.trim()) return false;
                                    const videoId = getYouTubeId(url);
                                    return isValidVideoId(videoId);
                                }).length || 0;

                                return (
                                    <div
                                        key={article.id}
                                        onClick={() => handleArticleClick(article)}
                                        style={{
                                            borderRadius: '0.75rem',
                                            border: `2px solid ${selectedArticle?.id === article.id ? '#9333ea' : '#e5e7eb'}`,
                                            background: 'white',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            overflow: 'hidden',
                                            boxShadow: selectedArticle?.id === article.id ? '0 4px 12px rgba(147, 51, 234, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (selectedArticle?.id !== article.id) {
                                                e.currentTarget.style.borderColor = '#c084fc';
                                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (selectedArticle?.id !== article.id) {
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }
                                        }}
                                    >
                                        {/* Video Thumbnail or Placeholder */}
                                        <div style={{
                                            position: 'relative',
                                            width: '100%',
                                            paddingBottom: '56.25%', // 16:9 aspect ratio
                                            height: 0,
                                            background: firstVideoId ? '#000' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            overflow: 'hidden'
                                        }}>
                                            {firstVideoId ? (
                                                <>
                                                    <img
                                                        src={`https://img.youtube.com/vi/${firstVideoId}/hqdefault.jpg`}
                                                        alt={article.title}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            backgroundColor: '#000'
                                                        }}
                                                        onError={(e) => {
                                                            const img = e.target as HTMLImageElement;
                                                            // Try fallback thumbnail sizes in order
                                                            if (img.src.includes('hqdefault') && !img.src.includes('mqdefault')) {
                                                                img.src = `https://img.youtube.com/vi/${firstVideoId}/mqdefault.jpg`;
                                                            } else if (img.src.includes('mqdefault') && !img.src.includes('sddefault')) {
                                                                img.src = `https://img.youtube.com/vi/${firstVideoId}/sddefault.jpg`;
                                                            } else if (img.src.includes('sddefault') && !img.src.includes('maxresdefault')) {
                                                                // Try maxresdefault as last resort
                                                                img.src = `https://img.youtube.com/vi/${firstVideoId}/maxresdefault.jpg`;
                                                            } else {
                                                                // If all thumbnails fail, show placeholder
                                                                img.style.display = 'none';
                                                                const container = img.parentElement;
                                                                if (container && !container.querySelector('.video-placeholder')) {
                                                                    const placeholder = document.createElement('div');
                                                                    placeholder.className = 'video-placeholder';
                                                                    placeholder.style.cssText = `
                                                                        position: absolute;
                                                                        top: 0;
                                                                        left: 0;
                                                                        width: 100%;
                                                                        height: 100%;
                                                                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                                                        display: flex;
                                                                        flex-direction: column;
                                                                        align-items: center;
                                                                        justify-content: center;
                                                                        color: white;
                                                                    `;
                                                                    placeholder.innerHTML = `
                                                                        <div style="font-size: 3rem; margin-bottom: 0.5rem;">🎥</div>
                                                                        <div style="font-size: 0.75rem; opacity: 0.9;">Video Preview Unavailable</div>
                                                                    `;
                                                                    container.appendChild(placeholder);
                                                                }
                                                            }
                                                        }}
                                                        onLoad={() => {
                                                            // Image loaded successfully
                                                            const img = document.querySelector(`img[alt="${article.title}"]`) as HTMLImageElement;
                                                            if (img) {
                                                                img.style.opacity = '1';
                                                            }
                                                        }}
                                                    />
                                                    {/* Play button overlay */}
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '60px',
                                                        height: '42px',
                                                        background: 'rgba(255, 0, 0, 0.9)',
                                                        borderRadius: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        pointerEvents: 'none'
                                                    }}>
                                                        <div style={{
                                                            width: 0,
                                                            height: 0,
                                                            borderLeft: '18px solid white',
                                                            borderTop: '11px solid transparent',
                                                            borderBottom: '11px solid transparent',
                                                            marginLeft: '3px'
                                                        }} />
                                                    </div>
                                                    {/* Video count badge */}
                                                    {videoCount > 1 && (
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '0.5rem',
                                                            right: '0.5rem',
                                                            background: 'rgba(0, 0, 0, 0.8)',
                                                            color: 'white',
                                                            padding: '0.25rem 0.5rem',
                                                            borderRadius: '0.25rem',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '0.25rem'
                                                        }}>
                                                            🎥 {videoCount}
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                // No video placeholder
                                                <div style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white'
                                                }}>
                                                    <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>
                                                        {categories.find(c => c.id === article.category)?.icon || '📚'}
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', opacity: 0.9 }}>
                                                        {categories.find(c => c.id === article.category)?.label}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Article Info */}
                                        <div style={{ padding: '0.875rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                            <h4 style={{ 
                                                fontSize: '0.9rem', 
                                                fontWeight: 600, 
                                                color: '#111827', 
                                                margin: 0,
                                                lineHeight: '1.4',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                minHeight: '2.8em'
                                            }}>
                                                {article.title}
                                            </h4>
                                            
                                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                <span style={{
                                                    padding: '0.125rem 0.375rem',
                                                    borderRadius: '0.25rem',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 500,
                                                    background: '#e5e7eb',
                                                    color: '#374151'
                                                }}>
                                                    {categories.find(c => c.id === article.category)?.icon} {categories.find(c => c.id === article.category)?.label}
                                                </span>
                                                {article.difficulty && (
                                                    <span style={{
                                                        padding: '0.125rem 0.375rem',
                                                        borderRadius: '0.25rem',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 500,
                                                        ...(article.difficulty === 'beginner' ? { background: '#dcfce7', color: '#166534' } :
                                                            article.difficulty === 'intermediate' ? { background: '#fef3c7', color: '#92400e' } :
                                                            { background: '#fee2e2', color: '#991b1b' })
                                                    }}>
                                                        {article.difficulty}
                                                    </span>
                                                )}
                                            </div>
                                            
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between', 
                                                alignItems: 'center', 
                                                fontSize: '0.7rem', 
                                                color: '#6b7280',
                                                paddingTop: '0.25rem',
                                                borderTop: '1px solid #f3f4f6'
                                            }}>
                                                <span>👁️ {article.views || 0} views</span>
                                                <span>❤️ {article.likes || 0}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

            </div>

            {/* Delete All Confirmation Modal */}
            {showDeleteAllConfirm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: 'white',
                        borderRadius: '0.75rem',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                    }}>
                        <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: '1rem' }}>
                            ⚠️ Delete All Articles
                        </h3>
                        <p style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                            Are you sure you want to delete <strong>ALL {articles.length} articles</strong>? This action cannot be undone.
                        </p>
                        <div style={{
                            background: '#fef3c7',
                            border: '1px solid #fde047',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            marginBottom: '1.5rem'
                        }}>
                            <p style={{ fontSize: '0.75rem', color: '#92400e', margin: 0 }}>
                                <strong>Warning:</strong> All articles will be permanently deleted. This action cannot be reversed.
                            </p>
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDeleteAllConfirm(false)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: '#f3f4f6',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteAll}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                }}
                            >
                                Delete All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Article Viewer Modal */}
            {selectedArticle && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    zIndex: 1100
                }}>
                    <div style={{
                        width: 'min(1200px, 100%)',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        background: 'white',
                        borderRadius: '0.75rem',
                        boxShadow: '0 25px 35px rgba(0,0,0,0.15)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <ArticleViewer
                            article={selectedArticle}
                            serviceProviderId={serviceProviderId}
                            userId={loggedInUserId}
                            onEdit={(article) => {
                                setEditingArticle(article);
                                setShowCreateForm(true);
                                setSelectedArticle(null);
                            }}
                            onDelete={handleDeleteArticle}
                            onBack={() => setSelectedArticle(null)}
                        />
                    </div>
                </div>
            )}

            {/* Create/Edit Article Modal */}
            {showCreateForm && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                    zIndex: 1100
                }}>
                    <div style={{
                        width: 'min(1100px, 100%)',
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        background: 'white',
                        borderRadius: '0.75rem',
                        boxShadow: '0 25px 35px rgba(0,0,0,0.15)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <ArticleEditor
                            article={editingArticle}
                            serviceProviderId={serviceProviderId}
                            userId={loggedInUserId}
                            userName={userName}
                            onSave={async () => {
                                setShowCreateForm(false);
                                setEditingArticle(null);
                                await loadArticles();
                            }}
                            onCancel={() => {
                                setShowCreateForm(false);
                                setEditingArticle(null);
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// Article Viewer Component
interface ArticleViewerProps {
    article: ChessArticle;
    serviceProviderId: string;
    userId: string;
    onEdit: (article: ChessArticle) => void;
    onDelete: (articleId: string) => void;
    onBack: () => void;
}

const ArticleViewer: React.FC<ArticleViewerProps> = ({ article, userId, onEdit, onDelete, onBack }) => {
    const canEdit = article.authorId === userId;
    const [playingVideoIndex, setPlayingVideoIndex] = useState<number | null>(null);

    return (
        <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            background: 'white',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{ 
                padding: '1.5rem', 
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb',
                position: 'relative'
            }}>
                {/* Top Row - Close Button and Action Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <button
                        onClick={onBack}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#6b7280',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            padding: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        ← Back to List
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {canEdit && (
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <button
                                    onClick={() => onEdit(article)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: '#9333ea',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Edit
                                </button>
                                <button
                                    onClick={() => article.id && onDelete(article.id)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        cursor: 'pointer'
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        )}
                        {/* Close Button - Top Right */}
                        <button
                            onClick={onBack}
                            style={{
                                background: 'transparent',
                                border: '1px solid #e5e7eb',
                                cursor: 'pointer',
                                color: '#6b7280',
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                width: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '0.375rem',
                                transition: 'all 0.2s',
                                lineHeight: '1'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#f3f4f6';
                                e.currentTarget.style.borderColor = '#d1d5db';
                                e.currentTarget.style.color = '#111827';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.borderColor = '#e5e7eb';
                                e.currentTarget.style.color = '#6b7280';
                            }}
                            title="Close"
                        >
                            ×
                        </button>
                    </div>
                </div>
                <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', marginBottom: '0.5rem' }}>
                    {article.title}
                </h1>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '0.375rem',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        background: '#e5e7eb',
                        color: '#374151'
                    }}>
                        {article.category}
                    </span>
                    {article.difficulty && (
                        <span style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            ...(article.difficulty === 'beginner' ? { background: '#dcfce7', color: '#166534' } :
                                article.difficulty === 'intermediate' ? { background: '#fef3c7', color: '#92400e' } :
                                { background: '#fee2e2', color: '#991b1b' })
                        }}>
                            {article.difficulty}
                        </span>
                    )}
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        By {article.authorName}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        👁️ {article.views || 0} views
                    </span>
                </div>
            </div>

            {/* Content */}
            <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '2rem',
                lineHeight: '1.8'
            }}>
                {/* YouTube Videos - Display in grid with titles */}
                {article.youtubeLinks && article.youtubeLinks.length > 0 ? (
                    <div style={{ 
                        marginBottom: '2rem', 
                        width: '100%'
                    }}>
                        <h3 style={{
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            color: '#111827',
                            marginBottom: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}>
                            🎥 Recommended Videos ({article.youtubeLinks.filter(url => url && url.trim()).length})
                        </h3>
                        
                        {/* Extract and validate YouTube video IDs */}
                        {(() => {
                            const getYouTubeId = (url: string): string | null => {
                                if (!url || !url.trim()) return null;
                                const trimmed = url.trim();
                                const patterns = [
                                    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/,
                                    /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
                                ];
                                for (const pattern of patterns) {
                                    const match = trimmed.match(pattern);
                                    if (match) return match[1];
                                }
                                return null;
                            };

                            // Validate video IDs - YouTube video IDs are always 11 characters
                            const isValidVideoId = (id: string): boolean => {
                                return id && id.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(id);
                            };

                            const validVideos = article.youtubeLinks
                                .map((url, idx) => {
                                    const videoId = getYouTubeId(url);
                                    // Only include if video ID is valid format
                                    if (videoId && isValidVideoId(videoId)) {
                                        return { videoId, index: idx, url };
                                    }
                                    return null;
                                })
                                .filter((v): v is { videoId: string; index: number; url: string } => v !== null);

                            if (validVideos.length === 0) {
                                return (
                                    <div style={{
                                        padding: '1.5rem',
                                        background: '#fef2f2',
                                        border: '1px solid #fecaca',
                                        borderRadius: '0.75rem',
                                        color: '#991b1b',
                                        fontSize: '0.875rem',
                                        textAlign: 'center'
                                    }}>
                                        ⚠️ No valid YouTube video links found. Please check the URLs.
                                    </div>
                                );
                            }

                            return (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                                    gap: '1.5rem',
                                    width: '100%'
                                }}>
                                    {validVideos.map(({ videoId, index }) => {
                                        const isPlaying = playingVideoIndex === index;
                                        
                                        return (
                                            <div key={index} style={{
                                                background: 'white',
                                                borderRadius: '0.75rem',
                                                border: '1px solid #e5e7eb',
                                                overflow: 'hidden',
                                                transition: 'all 0.3s ease',
                                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                display: 'flex',
                                                flexDirection: 'column'
                                            }}
                                            onMouseEnter={(event) => {
                                                event.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                                event.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={(event) => {
                                                event.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                                event.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                            >
                                                {/* Video Preview/Player */}
                                                <div style={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    paddingBottom: '56.25%', // 16:9 aspect ratio
                                                    height: 0,
                                                    background: '#000',
                                                    cursor: isPlaying ? 'default' : 'pointer'
                                                }}
                                                onClick={() => !isPlaying && setPlayingVideoIndex(index)}
                                                >
                                                    {isPlaying ? (
                                                        // Playing - show iframe
                                                        <iframe
                                                            src={`https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1`}
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                border: 'none'
                                                            }}
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                            allowFullScreen
                                                            title={`${article.title} - Video ${index + 1}`}
                                                        />
                                                    ) : (
                                                        // Thumbnail view
                                                        <>
                                                            <img
                                                                src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                                                                alt={`Video ${index + 1}`}
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    backgroundColor: '#1f2937'
                                                                }}
                                                                onError={(e) => {
                                                                    const img = e.target as HTMLImageElement;
                                                                    if (!img.src.includes('hqdefault')) {
                                                                        img.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                                                                    } else if (!img.src.includes('mqdefault')) {
                                                                        img.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                                                                    } else if (!img.src.includes('sddefault')) {
                                                                        img.src = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
                                                                    } else {
                                                                        img.style.display = 'none';
                                                                        const container = img.parentElement;
                                                                        if (container) {
                                                                            container.innerHTML = `
                                                                                <div style="
                                                                                    position: absolute;
                                                                                    top: 0;
                                                                                    left: 0;
                                                                                    width: 100%;
                                                                                    height: 100%;
                                                                                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                                                                                    display: flex;
                                                                                    flex-direction: column;
                                                                                    align-items: center;
                                                                                    justify-content: center;
                                                                                    color: white;
                                                                                    padding: 1rem;
                                                                                ">
                                                                                    <div style="font-size: 2.5rem; margin-bottom: 0.5rem;">🎥</div>
                                                                                    <div style="font-size: 0.875rem; text-align: center; font-weight: 500;">Video Unavailable</div>
                                                                                </div>
                                                                            `;
                                                                        }
                                                                    }
                                                                }}
                                                            />
                                                            {/* Play button overlay */}
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '68px',
                                                                height: '48px',
                                                                background: 'rgba(255, 0, 0, 0.9)',
                                                                borderRadius: '12px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                transition: 'all 0.2s ease',
                                                                pointerEvents: 'none'
                                                            }}>
                                                                <div style={{
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderLeft: '20px solid white',
                                                                    borderTop: '12px solid transparent',
                                                                    borderBottom: '12px solid transparent',
                                                                    marginLeft: '4px'
                                                                }} />
                                                            </div>
                                                            {/* Duration badge (placeholder) */}
                                                            <div style={{
                                                                position: 'absolute',
                                                                bottom: '0.5rem',
                                                                right: '0.5rem',
                                                                background: 'rgba(0, 0, 0, 0.8)',
                                                                color: 'white',
                                                                padding: '0.125rem 0.375rem',
                                                                borderRadius: '0.25rem',
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600
                                                            }}>
                                                                ▶
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                
                                                {/* Video Title and Info */}
                                                <div style={{
                                                    padding: '0.75rem',
                                                    background: 'white',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.5rem'
                                                }}>
                                                    <h4 style={{
                                                        fontSize: '0.875rem',
                                                        fontWeight: 600,
                                                        color: '#111827',
                                                        margin: 0,
                                                        lineHeight: '1.4',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden'
                                                    }}>
                                                        {article.title} - Part {index + 1}
                                                    </h4>
                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        fontSize: '0.75rem',
                                                        color: '#6b7280'
                                                    }}>
                                                        <span>Video {index + 1} of {validVideos.length}</span>
                                                        {isPlaying && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setPlayingVideoIndex(null);
                                                                }}
                                                                style={{
                                                                    background: '#ef4444',
                                                                    color: 'white',
                                                                    border: 'none',
                                                                    borderRadius: '0.25rem',
                                                                    padding: '0.25rem 0.5rem',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 500,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                Stop
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                ) : (
                    <div style={{
                        marginBottom: '2rem',
                        padding: '1.5rem',
                        background: '#f9fafb',
                        border: '1px dashed #d1d5db',
                        borderRadius: '0.75rem',
                        textAlign: 'center',
                        color: '#6b7280'
                    }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎥</div>
                        <p style={{ fontSize: '0.875rem', marginBottom: '0.25rem', fontWeight: 500 }}>
                            No videos available
                        </p>
                        <p style={{ fontSize: '0.75rem' }}>
                            Academy can add YouTube video links to enhance this article
                        </p>
                    </div>
                )}

                {/* Images - Display after videos */}
                {article.images && article.images.length > 0 && (
                    <div style={{ 
                        marginBottom: '2rem', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '1.5rem',
                        width: '100%'
                    }}>
                        {article.images.map((imageUrl, index) => {
                            if (!imageUrl || imageUrl.trim() === '') return null;
                            return (
                                <div key={index} style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    background: '#f9fafb',
                                    borderRadius: '0.75rem',
                                    padding: '1rem',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <img
                                        src={imageUrl.trim()}
                                        alt={`${article.title} - Image ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            maxWidth: '900px',
                                            height: 'auto',
                                            borderRadius: '0.5rem',
                                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                            objectFit: 'contain',
                                            display: 'block'
                                        }}
                                        onError={(e) => {
                                            console.error(`Failed to load image ${index + 1}:`, imageUrl);
                                            const imgElement = e.target as HTMLImageElement;
                                            imgElement.style.display = 'none';
                                            // Show error message
                                            const errorDiv = document.createElement('div');
                                            errorDiv.textContent = `Image ${index + 1} failed to load`;
                                            errorDiv.style.cssText = 'padding: 1rem; color: #ef4444; text-align: center; font-size: 0.875rem;';
                                            imgElement.parentElement?.appendChild(errorDiv);
                                        }}
                                        onLoad={() => {
                                            console.log(`Image ${index + 1} loaded successfully:`, imageUrl);
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Article Content */}
                <div 
                    dangerouslySetInnerHTML={{ __html: article.content }}
                    style={{
                        fontSize: '1rem',
                        color: '#374151',
                        maxWidth: '900px',
                        margin: '0 auto'
                    }}
                />

                {/* Tags */}
                {article.tags && article.tags.length > 0 && (
                    <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            {article.tags.map((tag, index) => (
                                <span
                                    key={index}
                                    style={{
                                        padding: '0.25rem 0.75rem',
                                        borderRadius: '0.375rem',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        background: '#f3e8ff',
                                        color: '#7c3aed'
                                    }}
                                >
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

// Article Editor Component
interface ArticleEditorProps {
    article: ChessArticle | null;
    serviceProviderId: string;
    userId: string;
    userName: string;
    onSave: () => void;
    onCancel: () => void;
}

const ArticleEditor: React.FC<ArticleEditorProps> = ({ article, serviceProviderId, userId, userName, onSave, onCancel }) => {
    const [title, setTitle] = useState(article?.title || '');
    const [category, setCategory] = useState<ChessArticle['category']>(article?.category || 'basics');
    const [content, setContent] = useState(article?.content || '');
    const [difficulty, setDifficulty] = useState<ChessArticle['difficulty']>(article?.difficulty || 'beginner');
    const [tags, setTags] = useState(article?.tags?.join(', ') || '');
    const [images, setImages] = useState(article?.images?.join('\n') || '');
    const [youtubeLinks, setYoutubeLinks] = useState(article?.youtubeLinks?.join('\n') || '');
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!title.trim() || !content.trim()) {
            toast.error('Please fill in title and content');
            return;
        }

        setSaving(true);
        try {
            const articleData: Omit<ChessArticle, 'id' | 'createdAt' | 'updatedAt'> = {
                title: title.trim(),
                category,
                content: content.trim(),
                difficulty,
                tags: tags.split(',').map(t => t.trim()).filter(t => t),
                images: images.split('\n').map(i => i.trim()).filter(i => i),
                youtubeLinks: youtubeLinks.split('\n').map(y => y.trim()).filter(y => y),
                authorId: userId,
                authorName: userName
            };

            if (article?.id) {
                // Update existing
                await updateChessArticle(serviceProviderId, article.id, articleData);
            } else {
                // Create new
                await createChessArticle(serviceProviderId, articleData);
            }
            
            onSave();
        } catch (error) {
            console.error('Error saving article:', error);
            toast.error('Failed to save article');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column', 
            background: 'white',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{ 
                padding: '1.5rem', 
                borderBottom: '1px solid #e5e7eb',
                background: '#f9fafb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                    {article ? 'Edit Article' : 'Create New Article'}
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={onCancel}
                        style={{
                            padding: '0.5rem 1rem',
                            background: 'transparent',
                            color: '#6b7280',
                            border: '1px solid #d1d5db',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        style={{
                            padding: '0.5rem 1rem',
                            background: saving ? '#9ca3af' : '#9333ea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            cursor: saving ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {saving ? 'Saving...' : 'Save Article'}
                    </button>
                </div>
            </div>

            {/* Form */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '800px' }}>
                    {/* Title */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                            Title *
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Enter article title"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    {/* Category and Difficulty */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                                Category *
                            </label>
                            <select
                                value={category}
                                onChange={(e) => setCategory(e.target.value as ChessArticle['category'])}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="basics">Chess Basics</option>
                                <option value="openings">Opening Strategies</option>
                                <option value="castling">Castle Strategies</option>
                                <option value="defense">Basic Defence</option>
                                <option value="tactics">Tactics</option>
                                <option value="endgame">Endgame</option>
                                <option value="strategy">Strategy</option>
                                <option value="other">Other</option>
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                                Difficulty
                            </label>
                            <select
                                value={difficulty}
                                onChange={(e) => setDifficulty(e.target.value as ChessArticle['difficulty'])}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '0.375rem',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="beginner">Beginner</option>
                                <option value="intermediate">Intermediate</option>
                                <option value="advanced">Advanced</option>
                            </select>
                        </div>
                    </div>

                    {/* Content */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                            Content * (HTML supported)
                        </label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder="Enter article content. You can use HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, etc."
                            style={{
                                width: '100%',
                                minHeight: '400px',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '1rem',
                                fontFamily: 'monospace',
                                resize: 'vertical'
                            }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            Tip: Use HTML tags for formatting. Example: &lt;h2&gt;Heading&lt;/h2&gt; &lt;p&gt;Paragraph&lt;/p&gt; &lt;ul&gt;&lt;li&gt;List item&lt;/li&gt;&lt;/ul&gt;
                        </p>
                    </div>

                    {/* YouTube Links */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                            YouTube Video Links (one per line)
                        </label>
                        <textarea
                            value={youtubeLinks}
                            onChange={(e) => setYoutubeLinks(e.target.value)}
                            placeholder="Enter YouTube video URLs, one per line. Examples:&#10;https://www.youtube.com/watch?v=VIDEO_ID&#10;https://youtu.be/VIDEO_ID"
                            style={{
                                width: '100%',
                                minHeight: '100px',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontFamily: 'monospace'
                            }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            Tip: Add YouTube video URLs from popular chess channels like GothamChess, Chess.com, Agadmator, etc.
                        </p>
                    </div>

                    {/* Images */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                            Image URLs (one per line) - Upload to Firebase Storage
                        </label>
                        <textarea
                            value={images}
                            onChange={(e) => setImages(e.target.value)}
                            placeholder="Enter image URLs from Firebase Storage, one per line"
                            style={{
                                width: '100%',
                                minHeight: '100px',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '0.875rem',
                                fontFamily: 'monospace'
                            }}
                        />
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                            Tip: Upload images to Firebase Storage and paste the download URLs here.
                        </p>
                    </div>

                    {/* Tags */}
                    <div>
                        <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                            Tags (comma-separated)
                        </label>
                        <input
                            type="text"
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="e.g., opening, strategy, tactics"
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '0.375rem',
                                fontSize: '1rem'
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChessBasicsView;

